import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { LoginControllerService } from "../../../services/server/LoginController.service";
import { ToastService } from "../../../services/shared/ToastService.service";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { trustImageDataUrl } from "../../../services/shared/html-sanitizer.util";
import { BrowserStorageLocalService } from "../../../services/shared/BrowserStorageServices/BrowserStorageLocalService.service";
import { ProfileService } from "../../../services/shared/ProfileService.service";
import { CUSTOM_URLS } from "../CUSTOM_URLS";
import { CustomUrlService } from "../../../services/shared/CustomUrl.service";
import { ContactService } from "../../../services/shared/ContactService.service";

import { PROFILES } from "../../../../assets/profiles/profiles";
import { LANGUAGE } from "../../../services/language/LanguageController.service";
import { UserConfig } from "../../../interfaces/UserGlobalInterface.type";
import { NgClass } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";

/**
 * Handles credential login, app/email two-factor authentication, and profile-based navigation.
 */
@Component({
  selector: "app-login-controller",
  templateUrl: "./login-controller.component.html",
  styleUrls: ["./login-controller.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, NgClass, TranslateModule, MatButtonModule]
})
export class LoginControllerComponent implements OnInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  // PHASE 1: Data verified, awaiting two-factor authentication
  authenticatorRequired: boolean = false;
  readonly comprobedUserResponseString: string = "require-2fa-setup"; // Server response when the user is verified but needs 2FA
  // PHASE 2.1: QR code required (2FA never registered)
  twoFactorAuthenticated: boolean = false;
  // PHASE 2.2: App code required (2FA already registered)
  twoFactorAppAuthenticated: boolean = false;
  // PHASE 3: Email code required
  twoFactorEmailAuthenticated: boolean = false;

  hidePassword: boolean = true;
  username: string;
  password: string;
  authenticatorKey: number;
  authenticatorCode: any;
  authenticatorQr: any;
  failLoginTimer: any;
  loginAttemps: number = 0;
  loginBlockedTime: number = 0;
  loginWating: boolean = false;
  /** Emits the landing route, or "change-password" when a required password update must start. */
  @Output() loginSucceed = new EventEmitter<string>();
  showAndroidLink: boolean = false;
  showIosLink: boolean = false;
  @ViewChild("authenticationKeyInput") authenticationKeyInput: ElementRef;
  @ViewChild("emailAuthCodeInput") emailAuthCodeInput: ElementRef;
  mailRegistered: boolean = false;
  defaultImg: string = "assets/img/logos/arsondata.png";
  logoUrl: string = CUSTOM_URLS.find((url: any) => window.location.href.includes(url.urlText))?.img;
  loginTitle: string = CUSTOM_URLS.find((url: any) => window.location.href.includes(url.urlText))
    ?.loginTitle;

  customContact = CUSTOM_URLS.find(url => window.location.href.includes(url.urlText))?.footer;
  authMethod: "app" | "email" | null = null;
  emailAuthCode: string = "";
  codeEmailSent: boolean = false;

  methodAccepted: boolean = false;

  resendCooldown: number = 0; // Remaining time before the next resend is allowed
  resendBaseCooldown: number = 30; // Base time (in seconds)
  resendMultiplier: number = 1; // Cooldown multiplier; increases on each resend
  resendInterval: any; // Countdown interval

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly BrowserStorageLocalService: BrowserStorageLocalService,
    private readonly ContactService: ContactService,
    private readonly CustomUrlService: CustomUrlService,
    private readonly domSanitizer: DomSanitizer,
    private readonly LoginController: LoginControllerService,
    private readonly ProfileService: ProfileService,
    private readonly SessionDataService: SessionDataService,
    private readonly ToastService: ToastService,
    private readonly translate: TranslateService
  ) {}

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    // Clear the previous customer's grouping before starting a new session.
    this.SessionDataService.clearAgrupation();

    // Applies client-specific title, favicon and theme from the URL
    this.CustomUrlService.checkCustomUrl();
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/
  // Verify credentials and either start two-factor authentication or complete login.
  loginFirst(): void {
    this.ToastService.closeToast();
    if (this.username && this.password) {
      this.username = this.username.trim();
      this.password = this.password.trim();
      this.loginWating = true;
      this.LoginController.login({
        username: this.username,
        password: this.password
      }).subscribe(response => {
        if (response["token"]) {
          if (response["token"] === this.comprobedUserResponseString) {
            this.authenticatorRequired = true;
            this.getAutheticatorTranslations(response["tr"]);
            if (response["mailRegistered"] != null) {
              this.mailRegistered = response["mailRegistered"];
              // Without a registered email, require app-based authentication.
              if (!this.mailRegistered) {
                this.authMethod = "app";
                this.generateGoogleCode();
                this.methodAccepted = true;
              }
            }
          } else {
            this.loginSucceedAction(response);
            this.SessionDataService.sendUserId(response["idUser"]);
          }
        } else {
          this.loginAttemps++;
          this.loginFailed(response["sleep"]);
        }
        this.loginWating = false;
      });
    }
  }

  getAutheticatorTranslations(translations: { [key: string]: string }): void {
    this.translate.setTranslation(this.translate.currentLang, translations, true);
  }

  onResendCode() {
    this.generateMailCode();

    this.resendCooldown = this.resendBaseCooldown * this.resendMultiplier;
    this.resendMultiplier++;

    if (this.resendInterval) clearInterval(this.resendInterval);

    this.resendInterval = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        clearInterval(this.resendInterval);
      }
    }, 1000);
  }

  // Submit the selected app or email second-factor code.
  public SubmitLogin2fa(): void {
    if (this.authMethod === "email") {
      this.login2faEmail();
    } else {
      this.login2faApp();
    }
  }

  public generateMailCode(): void {
    this.loginWating = true;
    this.LoginController.get2faMailCode({
      username: this.username,
      password: this.password
    }).subscribe(response => {
      if (response["status"] === "email_code_sent") {
        this.codeEmailSent = true;
        this.ToastService.fireToast("success", this.translate.instant("code-sent"));
      } else {
        this.ToastService.fireToast("error", this.translate.instant("error-sending-email"));
      }
      this.loginWating = false;
      setTimeout(() => this.emailAuthCodeInput?.nativeElement.focus(), 0);
    });
  }

  public generateGoogleCode(): void {
    this.loginWating = true;

    this.LoginController.get2faTOTPCode({
      username: this.username,
      password: this.password
    }).subscribe(response => {
      this.loginWating = false;
      if (response["qr"]) {
        const qrCode = response["qr"];
        this.authenticatorQr = trustImageDataUrl(this.domSanitizer, qrCode);
      }
      setTimeout(() => this.authenticationKeyInput?.nativeElement.focus(), 0);
    });
  }

  public confirmMFAMethod() {
    this.methodAccepted = true;
    if (this.authMethod === "email") {
      this.generateMailCode();
    } else {
      this.generateGoogleCode();
    }
  }

  login2faApp(): void {
    this.loginWating = true;
    this.LoginController.login2fa({
      code: this.authenticatorKey,
      username: this.username
    }).subscribe(response => {
      if (response["token"]) {
        this.loginSucceedAction(response);
        this.SessionDataService.sendUserId(response["idUser"]);
      } else {
        this.loginAttemps++;
        this.loginFailed(response["sleep"], true);
      }
      this.loginWating = false;
    });
  }

  login2faEmail() {
    this.loginWating = true;
    this.LoginController.login2faEmail({
      code: Number(this.emailAuthCode),
      username: this.username
    }).subscribe(response => {
      if (response["token"]) {
        this.loginSucceedAction(response);
        this.SessionDataService.sendUserId(response["idUser"]);
      } else {
        this.loginAttemps++;
        this.loginFailed(response["sleep"], true);
      }
      this.loginWating = false;
    });
  }

  // Apply login data, rejecting restricted profiles before establishing a session.
  loginSucceedAction(loginData: any): void {
    let userProfile: string = this.ProfileService.getProfile(loginData["perfil"]);

    switch (userProfile) {
      // case PROFILES.INSTALADOR:
      case PROFILES.API:
      case PROFILES.API_WALK_BY:
      case PROFILES.API_ON_PREMISE:
      case PROFILES.AQUACITY:
        this.ToastService.fireToast("error", this.translate.instant("access-denied-user"));
        break;
      case PROFILES.PERMANENT_TOKEN:
        this.ToastService.fireToast("error", this.translate.instant("access-denied"));
        break;
      default:
        this.setLanguage(loginData["language"]);
        this.BrowserStorageLocalService.setJsonValue("session", {
          token: loginData["token"],
          sessionStartDate: Date.now(),
          url: [],
          user: this.username,
          userId: loginData["idUser"],
          mustChangePassword: loginData["mustChangePassword"],
          lastLogin: loginData["lastLogin"]
        });
        this.SessionDataService.sendUser(this.username);
        this.SessionDataService.sendProfile(userProfile);
        this.disableHtmlTranslation(userProfile);
        this.SessionDataService.sendRemoteControlActive(loginData["isTlc"]);
        this.SessionDataService.sendTelemetryActive(loginData["isTlm"]);
        this.SessionDataService.sendLastLoginDate(loginData["lastLogin"]);
        // Original profile for the profile-switch feature
        this.SessionDataService.sendOriginalProfile(userProfile);

        // Update the URL or change password if needed
        if (loginData["mustChangePassword"]) {
          this.loginSucceed.emit("change-password");
        } else {
          this.SessionDataService.sendMustChangePassword(loginData["mustChangePassword"]);
          setTimeout(() => this.updateUrl(userProfile, loginData["isTlc"], loginData["isTlm"]), 0);
        }
        break;
    }
  }

  // Disable automatic browser translation
  disableHtmlTranslation(userProfile: any): void {
    if (userProfile !== PROFILES.ARSON) {
      document.documentElement.setAttribute("translate", "no");
    } else {
      document.documentElement.removeAttribute("translate");
    }
  }

  // Emit the landing route selected for the user's profile and enabled services.
  updateUrl(profile: string, isTlc: boolean, isTlm: boolean): void {
    let configData = this.getUserLocalConfig();
    if (configData?.mainPage) {
      this.SessionDataService.sendMainPageUpdatedFlag(configData.mainPage);
    }
    if (profile === PROFILES.ABONADO) {
      this.loginSucceed.emit("/abonado/principal");
    } else if (profile === PROFILES.ADMIN_CLIENTE) {
      this.loginSucceed.emit("/principal-entidades");
    } else if (profile === PROFILES.INSTALADOR) {
      this.loginSucceed.emit("/instalador/activacion");
    } else if (profile === PROFILES.ADMIN_INSTALLATION) {
      this.loginSucceed.emit("/dispositivos/listado/sustituciones");
    } else if (profile === PROFILES.USUARIO_TELEMEDIDA) {
      this.loginSucceed.emit("/principal");
    } else if (isTlc) {
      this.loginSucceed.emit("/telecontrol/alertas-activas");
    } else if (isTlm) {
      this.loginSucceed.emit("/telecontrol/telemedida");
    } else {
      // All other profiles always redirect to /principal
      // so MainRedirectGuard decides the home screen
      this.loginSucceed.emit("/principal");
    }
  }

  getUserLocalConfig(): UserConfig {
    let configData: UserConfig[] = this.BrowserStorageLocalService.getJsonValue("config");
    return configData?.find((config: UserConfig) => config.user === this.username);
  }

  // Normalize Catalan's API code and update the active session language.
  setLanguage(language: string): void {
    if (language === "CA") {
      language = LANGUAGE.CATALAN;
    }
    this.SessionDataService.sendLanguage(language.toLowerCase());
  }

  // Show credential/2FA failure feedback, including any server lockout duration.
  loginFailed(sleep: any, authentication?: boolean): void {
    if (sleep) {
      this.loginAttemps = 0;
      this.loginBlockedTime = sleep;
      this.loginBlocked();
      if (authentication) {
        this.ToastService.fireToast("error", this.translate.instant("authentication-error"));
      } else {
        this.ToastService.fireToast(
          "error",
          this.translate.instant("user-or-password-incorrect") +
            ". " +
            this.translate.instant("wait") +
            " (" +
            this.loginBlockedTime +
            "s)"
        );
      }
    } else if (authentication) {
      this.ToastService.fireToast(
        "error",
        this.translate.instant("authentication-error") + " (" + this.loginAttemps + "/" + 5 + ")"
      );
    } else {
      this.ToastService.fireToast(
        "error",
        this.translate.instant("user-or-password-incorrect") +
          " (" +
          this.loginAttemps +
          "/" +
          5 +
          ")"
      );
    }
  }

  // Login lockout countdown
  loginBlocked(): void {
    this.failLoginTimer = setInterval(() => {
      this.loginBlockedTime--;
      if (this.loginBlockedTime === 0) {
        clearInterval(this.failLoginTimer);
      }
    }, 1000);
  }

  // Open the contact details configured for the current login URL.
  showContact(): void {
    this.ContactService.showContact(this.customContact);
  }
}
